"use client";

import { usePathname } from "next/navigation";
import { motion } from "motion/react";
import { Section } from "./ui/section";
import { Title } from "./ui/title";
import { EASE } from "@/lib/motion";

const PAGE_ROMAN: Record<string, string> = {
  "o-nama": "I",
  usluge: "II",
  pacijenti: "III",
  ordinacija: "IV",
  galerija: "V",
  kontakt: "VI",
  blog: "VII",
};

const TOTAL_ROMAN = "VII";

type Props = {
  title: string;
  description: string;
};

function splitLastWord(text: string): [string, string] {
  const trimmed = text.trim();
  const lastSpace = trimmed.lastIndexOf(" ");
  if (lastSpace === -1) return ["", trimmed];
  return [trimmed.slice(0, lastSpace), trimmed.slice(lastSpace + 1)];
}

export function SecondaryHero({ title, description }: Props) {
  const pathname = usePathname();
  const segment = pathname?.split("/").filter(Boolean)[0] ?? "";
  const roman = PAGE_ROMAN[segment] ?? "·";

  const [head, tail] = splitLastWord(title);
  const isOneWord = head === "";

  return (
    <Section variant="hero" tone="inverse" className="overflow-hidden min-h-[55svh] flex flex-col">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.75, ease: EASE, delay: 0.1 }}
        className="flex items-center justify-between gap-6"
      >
        <span className="hidden md:inline-flex items-baseline gap-1.5 font-serif italic text-background/65 text-sm leading-none">
          <span className="uppercase tracking-[0.16em] text-[10px] not-italic font-sans text-background/45">
            Vol.
          </span>
          <span>{roman}</span>
          <span className="text-background/35">/ {TOTAL_ROMAN}</span>
        </span>
      </motion.div>

      <div className="flex-1" aria-hidden />

      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.95, ease: EASE, delay: 0.3 }}
          className="lg:col-span-8"
        >
          <Title
            variant="hero"
            tone="inverse"
            accent={isOneWord ? undefined : tail}
            className={isOneWord ? "max-w-none" : "max-w-[16ch]"}
          >
            {isOneWord ? tail : head}
          </Title>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.7 }}
          className="lg:col-span-4 flex flex-col gap-5"
        >
          <motion.span
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.85, ease: EASE, delay: 0.65 }}
            aria-hidden
            className="block h-px w-12 origin-left bg-background/55"
          />
          <p className="text-background/80 leading-[1.65] tracking-[-0.005em] text-[15px] md:text-base max-w-[420px]">
            {description}
          </p>
        </motion.div>
      </div>
    </Section>
  );
}
